<template>
  <div class="pb-2">
    <reservations-item
      v-if="$ftCS.DINING_USES_WALKINS"
      :value="selectedItemId"
      v-bind="{[newReservationType]: true}"
      :selected-table="table"
      :floor-plan-walk-in-data="{locationId: mealPeriod.locationId, date}"
      :on-close="() => selectedItemId = undefined"
      @after-save="doRefresh"
    />
    <left-side-panel-header
      v-bind="{isDirty, title}"
      compact-mode-breakpoint="md"
      @click:back="$emit('deselect-open-table')"
      @click:save="save"
    />
    <api-errors v-bind="{errors}" />
    <v-card
      v-if="table"
      class="ma-2 pa-2"
    >
      <v-row
        no-gutters
        class="font-weight-bold"
      >
        <v-col
          align-self="center"
          cols="3"
          class="d-flex justify-center"
        >
          {{ `${table.minCovers}-${table.maxCovers}` }}
        </v-col>
        <v-col
          align-self="center"
          cols="6"
          class="d-flex justify-center"
        >
          OPEN TABLE
        </v-col>
        <v-col
          cols="3"
          class="d-flex justify-end"
        >
          <table-names-card
            :table-names="table.name"
            left
          />
        </v-col>
      </v-row>
    </v-card>
    <v-row no-gutters>
      <v-col
        cols="6"
        class="pt-3 px-3"
      >
        <v-tooltip
          top
          color="primary"
        >
          <template #activator="{ attrs, on }">
            <div
              v-bind="$ftCS.DINING_USES_WALKINS ? {} : attrs"
              v-on="$ftCS.DINING_USES_WALKINS ? {} : on"
            >
              <v-card
                v-if="mealPeriod.isFuture"
                class="d-flex justify-center font-weight-bold px-2 py-3"
                @click="selectedItemId = null"
              >
                <v-icon class="black--text pr-2">
                  mdi-silverware-fork-knife
                </v-icon>
                <span>Add Reservation</span>
              </v-card>
              <v-card
                v-else
                :disabled="!$ftCS.DINING_USES_WALKINS || mealPeriod.isPast"
                class="d-flex justify-center font-weight-bold px-2 py-3"
                @click="selectedItemId = null"
              >
                <v-icon class="black--text pr-2">
                  mdi-walk
                </v-icon>
                <span>Add Walk-In</span>
              </v-card>
            </div>
          </template>
          <span>Walk-In Reservations Disabled</span>
        </v-tooltip>
      </v-col>
      <v-col
        cols="6"
        class="pt-3 px-3"
      >
        <v-card
          class="d-flex justify-center font-weight-bold px-2 py-3"
          :class="{'primary lighten-3': status === 'BLOCKED'}"
          @click="setStatus('BLOCKED')"
        >
          <v-icon class="black--text pr-2">
            mdi-cancel
          </v-icon>
          <span>Block Table</span>
        </v-card>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col
        cols="6"
        class="pt-3 px-3"
      >
        <v-card
          class="d-flex justify-center font-weight-bold px-2 py-3"
          :class="{'primary lighten-3': status === 'FINISHED'}"
          @click="setStatus('FINISHED')"
        >
          <v-icon class="black--text pr-2">
            mdi-check
          </v-icon>
          <span>Finished</span>
        </v-card>
      </v-col>
      <v-col
        cols="6"
        class="pt-3 px-3"
      >
        <v-card
          class="d-flex justify-center font-weight-bold px-2 py-3"
          :class="{'primary lighten-3': status === 'NEEDS_BUSSING'}"
          @click="setStatus('NEEDS_BUSSING')"
        >
          <v-icon class="black--text pr-2">
            mdi-broom
          </v-icon>
          <span>Bussing Needed</span>
        </v-card>
      </v-col>
    </v-row>
    <scaler
      v-slot="{ fontSize, transition }"
      y-axis="1.3"
      class="pt-3 px-3"
    >
      <div
        :style="{'font-size': fontSize, transition}"
        class="text-center font-weight-bold"
      >
        Assigned Server
      </div>
    </scaler>
    <loading-bar v-if="isLoading" />
    <v-card
      v-else-if="!(servers || []).length"
      class="pa-2 mt-2 mx-2"
    >
      <div class="text-center font-weight-bold">
        No Server Assigned!
      </div>
    </v-card>
    <v-card
      v-for="server of servers"
      :key="`server-assignment-${(server || {}).id}`"
      class="pa-2 mt-2 mx-2"
    >
      <div class="d-flex justify-center title">
        <server-initial-icon
          :server="server"
          large
          class="pr-2"
        />
        <div :class="{'text-decoration-line-through': !server.active}">
          {{ server.name }}
        </div>
      </div>
    </v-card>
    <v-card
      class="pa-0 ma-0 mt-1"
      elevation="0"
      :disabled="!table"
    >
      <scaler
        v-slot="{ fontSize, transition }"
        y-axis="1.3"
        class="pt-3 px-3 font-weight-bold"
      >
        <div
          :style="{'font-size': fontSize, transition}"
          class="text-center"
        >
          Next Party
        </div>
      </scaler>
      <reservation-card
        v-if="nextPartyReservation"
        :reservation="nextPartyReservation"
        :ripple="false"
        :style="{cursor: 'default'}"
        class="ma-2"
      />
      <v-card
        v-else
        class="mx-2 mt-2"
      >
        <div class="text-center font-weight-bold pa-3">
          No Next Party Found
        </div>
      </v-card>
    </v-card>
    <div v-if="table">
      <scaler
        v-slot="{ fontSize, transition }"
        y-axis="1.3"
        class="pt-3 px-3 font-weight-bold"
      >
        <div
          class="text-center"
          :style="{'font-size': fontSize, transition}"
        >
          Schedule of Table
        </div>
      </scaler>
      <table-schedule-chart
        v-bind="{
          date,
          tableId: table.id,
          tableName: table.tableName,
          min: getMealPeriodStartTime(mealPeriod),
          max: getMealPeriodEndTime(mealPeriod),
          reservationLength: mealPeriod.timeIncrement || 30,
          mealPeriodId: mealPeriod.id,
        }"
      />
    </div>
    <simple-expansion-card v-else>
      <template #title>
        Schedule of Tables
      </template>
      <template #persistent-content>
        <div class="px-3 pt-1">
          {{ tables[0].name }}
        </div>
        <table-schedule-chart
          v-bind="{date, ...tables[0]}"
          :min="getMealPeriodStartTime(mealPeriod)"
          :max="getMealPeriodEndTime(mealPeriod)"
          :reservation-length="mealPeriod.timeIncrement || 30"
          :meal-period-id="mealPeriod.id"
        />
      </template>
      <div
        v-for="(t, index) of tables"
        :key="`table-schedule-${t.id}`"
      >
        <div v-if="!!index">
          <div class="px-3 pt-1">
            {{ t.name }}
          </div>
          <table-schedule-chart
            v-bind="{date, ...t}"
            :min="getMealPeriodStartTime(mealPeriod)"
            :max="getMealPeriodEndTime(mealPeriod)"
            :reservation-length="mealPeriod.timeIncrement || 30"
            :meal-period-id="mealPeriod.id"
          />
        </div>
      </div>
    </simple-expansion-card>
  </div>
</template>

<script>
import floorPlanHelpers from '../mixins/floorPlanHelpers'
import mealPeriodHelpers from '../mixins/mealPeriodHelpers'
import {deepClone} from '@/ft-vue-core/util/helpers'
import apiHelpers from '@/ft-vue-core/mixins/itemEditor/mixins/apiHelpers'
import Scaler from '@/ft-vue-core/components/transformers/Scaler'
import TableScheduleChart from '../helperComponents/TableScheduleChart'
import ReservationCard from '../leftSidePanel/reservationsTab/ReservationCard'
import ReservationsItem from '../../reservations/ReservationsItem.vue'
import ServerInitialIcon from '../helperComponents/ServerInitialIcon.vue'
export default {
  name: 'SelectedOpenTable',
  components: {Scaler, TableScheduleChart, ReservationCard, ReservationsItem, ServerInitialIcon},
  mixins: [apiHelpers, floorPlanHelpers, mealPeriodHelpers],
  props: {
    tables: {
      type: Array,
      required: true
    },
    mealPeriod: {
      type: Object,
      default () {return {}}
    },
    serverAssignmentsMap: Object,
    date: [String, Number]
  },
  data () {
    return {
      isDirty: false,
      itemData: [],
      /** DiningReservation object used to display the next party on the selected table  */
      nextPartyReservation: undefined,
      /** Utility to reset watchers when `tables.length` changes */
      watchStoppers: {},
      /** Item ID used to trigger creation of new walk-in reservations */
      selectedItemId: undefined,
    }
  },
  computed: {
    newReservationType () {
      return this.mealPeriod.isFuture ? 'addReservation' : 'addWalkIn'
    },
    servers () {
      const {id} = this.table ?? {}
      return this.serverAssignmentsMap[id]
    },
    /** CSV generated from the names of all selected tables */
    title () {
      return this.tables.reduce((acc, r) => {
        if (acc.length) acc += ', '
        acc += r.name || ''
        return acc
      }, '') || ''
    },
    /**
     * @returns {Object} IF there is exactly 1 selected open table, this is it.
     * */
    table () {
      if (this.tables?.length === 1) return this.tables[0]
      return null
    },
    /**
     * @returns {String} The status of all tables IF all tables' statuses are the same.
     * ELSE return `null`
     * @see `set` The status of ALL selected tables is set to the same value
     */
    status: {
      get () {
        const {status} = this.itemData[0] ?? {}
        for (let table of this.itemData?.slice(1, this.itemData?.length)) {
          if (status !== table.status) return null
        }
        return status
      },
      set (value) {
        this.itemData = this.itemData.map(e => {
          e.status = value
          return e
        })
      }
    }
  },
  /**
   * When mounted:
   *    Set `itemData` to a deepClone of `tables` and watch for changes to `itemData`
   *    Set a watcher on `tables.length` to create side-effects when tables are added/removed
   *    When tables are added/removed, rebase `itemData`,  re-establish it's watcher and gather data from API again
   */
  mounted () {
    this.itemData = deepClone(this.tables)
    const watchItemData = () => this.watchStoppers.itemData = this.$watch('itemData', () => this.isDirty = true, {deep: true})
    watchItemData()
    this.findNextParty(this.itemData[0] || {})
  },
  methods: {
    setStatus (status) {
      if (this.status === status) this.status = undefined
      else this.status = status
    },
    doRefresh () {
      setTimeout(() => this.$emit('deselect-open-table'), 200)
      setTimeout(() => this.$emit('refresh-tables'))
    },
    /**
     * Issue a command to the API to get the next reservation on selected tables
     * @param {Object} id ID used to find next reservation at Table
     */
    findNextParty ({id: tableId}) {
      this.$ftAPI().get({
        command: 'diningReservation',
        load: r => this.nextPartyReservation = r[0],
        afterLoad: () => this.awaitingApiResponse -= 1,
        error: r => {
          this.awaitingApiResponse -= 1
          this.errors.push(r.message)
        },
        params: {
          children: true,
          order_by: ['dateTime:ASC'],
          include_fields: ['diningReservationDetail.member'],
          filter_methods: {
            filterByAfterTimeNow: [],
            filterByDate: [this.date],
            filterByTableAssignments: [[tableId]]
          }
        }
      })
    },
    /** Set status  of table with call to `diningSetTableStatus` */
    save () {
      this.$ftAPI().put(this.addResourceErrorHandler({
        command: 'diningSetTableStatus',
        loading: this.setIsLoading,
        afterLoad: () => {
          this.$emit('refresh-tables')
          this.$emit('deselect-open-table')
        }
      }), {tableIds: this.itemData.map(t => t.id), status: this.status})
    }
  }
}
</script>

<style scoped>
.v-card::before {
  background: none;
}
.v-card {
  transition: background-color 0.2s ease-in-out;
}
</style>
